import store from "@/store";
import { EWebMethod, IRequest } from "@/struct/IRequest/IRequest";
import UtilStorage from "@/util/Common/UtilStorage";
import { App } from "@capacitor/app";
import { Capacitor, HttpResponse } from "@capacitor/core";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as Sentry from "@sentry/vue";

class $api {
  private static fingerprint: string | null = null;
  private static fingerprintKey = "saved_fingerprint";

  private static async getFingerprint(): Promise<string> {
    // Сначала проверяем, есть ли сохраненный fingerprint в хранилище
    const storedFp = await UtilStorage.getItem(this.fingerprintKey);

    if (storedFp) {
      // Fingerprint уже был сгенерирован и сохранен ранее — используем сохраненный
      // Это обеспечивает стабильность across сессий и обновлений приложения
      this.fingerprint = storedFp;
      return this.fingerprint;
    }

    // Если fingerprint не сохранен (первый launch или очистка кэша)
    if (!this.fingerprint) {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.fingerprint = result.visitorId;

      // Сохраняем fingerprint в платформо-зависимое хранилище
      // UtilStorage сам выбирает: Preferences для native (Android/iOS), localStorage для web/PWA
      await UtilStorage.setItem(this.fingerprintKey, this.fingerprint);
    }

    return this.fingerprint;
  }

  private static getBaseUrl(): string {
    if (process.env.VUE_APP_BASE_URL) {
      return process.env.VUE_APP_BASE_URL.trim().replace(/\/+$/, "");
    }

    return window.location.origin;
  }

  private static readonly SKIP_RECOVERY_URLS = ["auth/is-telegram"];

  private static shouldSkipRecovery(url: string): boolean {
    return this.SKIP_RECOVERY_URLS.some((skipUrl) => url.includes(skipUrl));
  }

    private static normalizeUrl(url: string): string {
    // Очищаем от пробелов и неразрывных пробелов
    url = url.replace(/\s+/g, "").replace(/\u00A0/g, "");

    // Если URL уже абсолютный, возвращаем как есть
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Получаем правильный базовый URL из .env (https://pro.force2.ru)
    const baseUrl = this.getBaseUrl();

    let finalUrl = "";

    if (
      process.env.VUE_APP_BRAND_ID == "82e8c1ebec3b4b86a16c29a550f4854e" &&
      store.state.brandStatus?.IsDev === true
    ) {
      // Убедимся, что между baseUrl и путем есть слеш
      finalUrl = baseUrl + (url.startsWith('/') ? url : '/' + url).replace(/^\/dev/, '/dev');
    } else {
      // ИСПРАВЛЕНИЕ: Добавляем baseUrl к относительному пути
      finalUrl = baseUrl + (url.startsWith('/') ? url : '/' + url);
    }

    return finalUrl.trim().replace(/\/+$/, "");
  }

  private static async makeNetworkRequest(
    options: any,
    method: EWebMethod,
    url: string,
  ): Promise<HttpResponse> {
    try {
      let finalUrl = url;

      if (options.params && method === EWebMethod.GET) {
        const searchParams = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        const queryString = searchParams.toString();
        if (queryString) {
          finalUrl += (url.includes('?') ? '&' : '?') + queryString;
        }
      }

      const fetchOptions: RequestInit = {
        method: method,
        headers: options.headers,
        credentials: "include",
      };

      if (options.data && method !== EWebMethod.GET) {
        fetchOptions.body = typeof options.data === "string" ? options.data : JSON.stringify(options.data);
      }

      const fetchResponse = await fetch(finalUrl, fetchOptions);

      let data;
      try {
        data = await fetchResponse.json();
      } catch (e) {
        data = { detail: "incorrect_response", status: fetchResponse.status };
      }

      return {
        url: url,
        status: fetchResponse.status,
        data: data,
        headers: Object.fromEntries(fetchResponse.headers.entries()),
      };
    } catch (fetchError) {
      console.error("Fetch request failed:", fetchError);

      const networkError = new TypeError(
        "Network request failed (offline state)",
      );

      (networkError as any).originalFetchError = fetchError;

      throw networkError;
    }
  }

  public static async call(
    method: EWebMethod,
    url: string,
    data?: any,
    retryOnUnauthorized = true,
  ): Promise<any | HttpResponse> {
    try {
      const fingerprint = await this.getFingerprint();
      const platform = Capacitor.getPlatform();

      url = this.normalizeUrl(url);

      const options: IRequest = {
        url: url,
        headers: {
          "X-App-Version-id": store.state.currentAppVersion,
          "Content-Type": "application/json",
          "X-App-Type": "driver",
          "X-App-Platform": platform,
          "X-Timezone-Offset": new Date().getTimezoneOffset().toString(),
          Accept: "*/*",
          Connection: "keep-alive",
          "X-Fingerprint": fingerprint,
          "X-Brand-Id": process.env.VUE_APP_BRAND_ID,
        },
      };

      if (data) {
        method === EWebMethod.GET
          ? (options.params = data)
          : (options.data = data);
      }

      try {
        options.headers["User-Agent"] = navigator.userAgent;
      } catch (e) {
        console.error(e);
        options.headers["User-Agent"] = "Unknown";
      }

      options.headers.AppPlatform = platform;

      if (platform !== "web") {
        try {
          const appInfo = await App.getInfo();
          options.headers.AppBuild = appInfo.build;
        } catch (e) {
          console.warn("Failed to get App info", e);
        }
      }

      let token: string | null = null;
      let secretKey: string | null = null;
      try {
        token = await UtilStorage.getItem("token");
        secretKey = await UtilStorage.getItem("secret_key");
      } catch (e) {
        console.warn("Failed to read auth data from storage", e);
      }

      if (token) {
        options.headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await this.makeNetworkRequest(options, method, url);

      const newSecretKey =
        response.headers["x-secret-key"] || response.headers["X-Secret-Key"];
      if (newSecretKey) {
        try {
          await UtilStorage.setItem("secret_key", newSecretKey);
        } catch (e) {
          console.warn("Failed to save secret_key", e);
        }
      }

      // Пробрасываем request-id бека (CORS: X-Request-ID открыт через
      // access-control-expose-headers, fetch отдает ключи в нижнем регистре).
      // Кладем в тело ответа, т.к. wrapper'ы возвращают только response.data
      // и заголовки до view не доходят. Только для объектов (не массивы),
      // поле бека не затираем. Нужно модалкам ошибок (ID запроса) и Sentry.
      const backendRequestId =
        response.headers["x-request-id"] ||
        response.headers["X-Request-ID"];
      if (
        typeof backendRequestId === "string" &&
        backendRequestId.trim() &&
        response.data &&
        typeof response.data === "object" &&
        !Array.isArray(response.data) &&
        !("request_id" in response.data)
      ) {
        (response.data as Record<string, unknown>).request_id =
          backendRequestId.trim();
      }

      if (!response || response.status === 408) {
        Sentry.captureMessage(`408 timeout: ${url}`);
        return {
          url: url,
          status: 0,
          data: { detail: "time_out" },
        };
      }

      if (response.status === 401 && retryOnUnauthorized) {
        if (this.shouldSkipRecovery(url)) {
          return response;
        }

        if (secretKey) {
          try {
            const recoveryResponse = await this.call(
              EWebMethod.POST,
              "auth/user/login/recovery-cookies",
              { token: secretKey },
              false,
            );

            if (recoveryResponse.status === 200) {
              return this.call(method, url, data, false);
            }
          } catch (e) {
            console.error(e);
            Sentry.captureException(e);
          }
        }

        try {
          await UtilStorage.clearAuthData();
        } catch (e) {
          console.warn("Failed to clear auth data", e);
        }
        window.location.href = "/";
      }

      if (response && ![200, 201, 204].includes(response.status)) {
        if (![401, 429].includes(response.status)) {
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTag("Interceptor", "http_error");
            scope.setTag("platform", Capacitor.getPlatform());
            scope.setTag("status", String(response.status));
            scope.setContext("api_request", {
              url,
              method,
              data: data ? JSON.stringify(data).substring(0, 500) : null,
            });
            scope.setContext("api_response", {
              status: response.status,
              data: response.data
                ? JSON.stringify(response.data).substring(0, 1000)
                : null,
              headers: response.headers,
              request_id:
                response.headers["x-request-id"] ||
                response.headers["X-Request-ID"] ||
                null,
            });
            Sentry.captureMessage(`HTTP Error: ${url} [${response.status}]`);
          });
        }
      }

      return response;
    } catch (error) {
      let detail = "internal_error";
      const message = error instanceof Error ? error.message : String(error);

      const isNetworkIssue =
        /offline|network|connection|fetch|dns|internet/i.test(message);

      if (
        error instanceof TypeError ||
        isNetworkIssue ||
        (error instanceof Error &&
          (/Failed to fetch/i.test(message) ||
            /Network request failed/i.test(message) ||
            /net::ERR_INTERNET_DISCONNECTED/i.test(message) ||
            /net::ERR_NAME_NOT_RESOLVED/i.test(message) ||
            /net::ERR_NETWORK_CHANGED/i.test(message) ||
            /net::ERR_PROXY_CONNECTION_FAILED/i.test(message)))
      ) {
        detail = "network_error";
      } else if (
        message.includes("UtilStorage") ||
        message.includes("getItem") ||
        message.includes("setItem") ||
        message.includes("localStorage") ||
        message.includes("secure storage")
      ) {
        detail = "storage_error";
      } else if (
        message.includes("Capacitor") ||
        message.includes("App.getInfo") ||
        message.includes("getPlatform")
      ) {
        detail = "platform_error";
      } else if (message.includes("getFingerprint")) {
        detail = "fingerprint_error";
      }

      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setTag("Interceptor", detail);
        scope.setTag("platform", Capacitor.getPlatform());
        scope.setContext("api_request", {
          url,
          method,
        });
        scope.setContext("error_details", {
          message,
          stack: error instanceof Error ? error.stack : null,
          original_error: error,
        });
        Sentry.captureMessage(`API Critical Error: ${url} [${detail}]`);
      });

      return {
        status: 0,
        data: {
          detail,
          message,
        },
        url: url,
      };
    }
  }
}

export default $api;
